import { GridReadyEvent, GridOptions, ColDef, RowSelectedEvent, EditableCallbackParams } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SaveAndQueryComponent, GridOptionsExt, BaseService, StatusService, AgFns, ProxAgFns, ISortModel } from '@core';
import { EntityFns } from '@data';
import { Account, ActiveStatusEnum, KitTag } from '@models';
import { UtilFns } from '@utils';
import * as _ from 'lodash';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { EntityError } from 'breeze-client';

@Component({
  selector: 'prox-program-kit-tags',
  standalone: false,
  templateUrl: './program-kit-tags.component.html',
})
export class ProgramKitTagsComponent extends SaveAndQueryComponent {
  accountId!: string;
  account!: Account;
  tagGridOptions!: GridOptionsExt;
  tags?: KitTag[];
  selectedTag?: KitTag;

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    private statusService: StatusService,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    this.account = await this.dbQueryService.getAccountById(this.accountId);
    UtilFns.assertNonNull(this.account, 'Account');

    this.tagGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onTagGridReady,
      onRowSelected: this.onTagRowSelected,
    });
    AgFns.captureGridRouteParams(this.tagGridOptions, this.route, 'id');

    this.tags = await this.dbQueryService.getKitTagsForAccount(this.accountId);
    this.setTitle('Account Kit Tags for ' + this.account.name);
    this.isPageReady = true;
  }

  statusMessage() {
    let msg = '';
    if (this.account?.activeStatusId == ActiveStatusEnum.Active) {
      return 'Account is Active and Not Editable.  To edit, set Account status to Hold.';
    } else if (this.account?.activeStatusId == ActiveStatusEnum.Hold) {
      msg = 'Account is On-Hold and is Editable';
    } else {
      return 'Account is ' + this.account?.activeStatus.name + ' and is Not Editable';
    }
    return msg;
  }

  isReadOnly() {
    return this.statusService.getWorkingStatus(this.account).isActiveReadOnly;
  }

  canDelete() {
    return !this.isReadOnly();
  }

  // Tag Grid

  onTagGridReady(event: GridReadyEvent) {
    const gridOptions = event.context.gridOptions as GridOptions;
    const [colDefs, sortModel] = this.getTagColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.applyGridRouteParams(gridOptions);
  }

  getTagColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      ProxAgFns.getEntityStateColDef(),
      { headerName: 'Name', field: 'name', editable: !this.isReadOnly(), filter: 'agTextColumnFilter' },
      ProxAgFns.getEntityDeleteColDef(this.onTagDelete.bind(this), { canDisplay: this.canDelete.bind(this) }),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  async onTagRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const tag = event.data as KitTag;
    this.selectedTag = tag;
    if (tag == null) return;

    if (tag.entityAspect.entityState.isDeleted()) {
      this.selectedTag = undefined;
      this.tagGridOptions.apiX?.deselectAll();
      return;
    }

    this.updateGridRouteParams(this.tagGridOptions, tag.id);
  }

  async onTagAdd() {
    const tag = this.dbSaveService.createEntity(KitTag, { accountId: this.accountId });
    tag.entityAspect.validateEntity();
    this.tags?.push(tag);
    AgFns.refreshGrid(this.tagGridOptions, this.tags);
    AgFns.selectGridRowByKey(this.tagGridOptions, (e: KitTag) => e.id, tag.id, 'name');
  }

  async onTagDelete(tag: KitTag) {
    /*     if (tag.entityAspect.entityState.isAdded()) {
      tag. programKitTagMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    } */

    EntityFns.deleteOrDetach(tag.entityAspect);
    this.selectedTag = undefined;
    if (this.tags) {
      _.remove(this.tags, tag);
      AgFns.refreshGrid(this.tagGridOptions, this.tags);
    }
  }

  isTagEditable(params: EditableCallbackParams) {
    if (!this.selectedTag) return false;
    return this.selectedTag['_tmpCanDelete'] ?? false;
  }

  // ---- Misc ----------------------------

  override async navigateToValidationError(error: EntityError) {
    const errEnt = error.entity;
    if (errEnt instanceof KitTag) {
      AgFns.selectGridRowByKey(this.tagGridOptions, (e: KitTag) => e.id, errEnt.id, error.propertyName);
    }
  }

  override async addCrossValidationErrors() {
    //
  }

  override async afterSave() {
    return this.afterUndo();
  }

  override async afterUndo() {
    if (this.tags) {
      this.tags = await this.dbQueryService.getKitTagsForAccount(this.accountId);
    }

    AgFns.refreshGrid(this.tagGridOptions, this.tags);

    if (!this.tagGridOptions.apiX) return;
    if (!this.selectedTag) {
      AgFns.selectFirstRow(this.tagGridOptions);
    } else {
      AgFns.selectGridRowByKey(this.tagGridOptions.apiX, (e) => e.id, this.selectedTag.id);
    }
  }
}
